import React from 'react';
import PropTypes from 'prop-types';
import FieldSelector from 'components/FieldSelector/FieldSelector';
import SlateDatePicker from 'components/Shared/SlateDatePicker/SlateDatePicker';
import { getTimestampFromDate, getZonedTimestamp, getZonedCalendarDate, getBrowserTimeZone } from 'helpers/dateTimeHelper';
import { isDayCoarse, effectiveHoursOfDay, parseNonNegativeInt } from 'helpers/recurrenceLimits';
import { dynamicDate, fieldsTypes } from 'config/constants';

var ANCHORS = ['today', 'date', 'field'];

var OFFSET_UNITS = ['days', 'weeks', 'months', 'years'];

// Sanity ceiling for the anchor offset ("N <unit> before/after" the anchor
// field's value) -- a different concept from the per-frequency recurrence
// interval in helpers/recurrenceLimits.js (MAX_INTERVAL), which the API DOES
// enforce and mirror number-for-number. This offset has no such counterpart:
// the API only requires it to be >= 0 with a recognized unit (see
// InspectionService+Update.cs and InspectionSchema.js), no matter how large.
// 3660 is carried over from the dashboard's old flat recurrence-interval
// ceiling (retired in Task 10 for per-frequency caps) purely as a generous,
// arbitrary bound so the number input can't grow without limit -- not a value
// any other file agrees with, so it must not be derived from MAX_INTERVAL.
var MAX_FIRST_DUE_OFFSET = 3660;

// A records field's Children are the associated record template's own fields
// (see AccountService+Fields.cs BuildAccountFieldResource), nested arbitrarily
// deep through groups. Those fields describe the RECORD, not the asset, so
// they can never anchor an asset's own schedule -- this drops a records field
// and everything nested under it, keeping every other field (including
// regular groups of asset fields) untouched. Exported so Manual mode's own
// date-field picker (sections/Recurrence.js) can scope its options the same
// way.
export var scopeToAssetFields = function (fields) {
  if (!fields) return [];
  var result = [];
  for (var i = 0; i < fields.length; i++) {
    var field = fields[i];
    if (!field || field.type === fieldsTypes.RECORDS) continue;
    if (field.children) {
      result.push(Object.assign({}, field, { children: scopeToAssetFields(field.children) }));
    } else {
      result.push(field);
    }
  }
  return result;
};

var FirstDueDateEditor = function (props) {
  var anchor = props.anchor || 'today';
  var date = props.date;
  var fieldKey = props.fieldKey;
  var offsetInterval = props.offsetInterval;
  var offsetUnit = props.offsetUnit || 'days';
  var settingsFields = props.settingsFields || [];
  var timeZone = props.timeZone || getBrowserTimeZone();
  var hoursOfDay = effectiveHoursOfDay(props.frequency, props.hoursOfDay);
  var onChange = props.onChange;
  var t = props.t;
  var errors = props.errors || {};

  // The stored date is an instant in the rule's zone, but SlateDatePicker
  // formats and selects in browser-local time, so hand it browser-local
  // midnight of the calendar day the instant falls on in the rule's zone.
  // Otherwise a browser west of the rule's zone shows the previous day.
  var pickerDate = typeof date === 'number' ? getTimestampFromDate(getZonedCalendarDate(date, timeZone)) : date;

  var handleAnchorChange = function (nextAnchor) {
    // Switching anchor drops the values the other anchors own, so a saved rule
    // never carries a first-due date and a field hash at the same time.
    var partial = { firstDueAnchor: nextAnchor };
    if (nextAnchor !== 'date') partial.firstDueDate = null;
    if (nextAnchor !== 'field') {
      partial.firstDueFieldKey = null;
      partial.firstDueFieldTitle = '';
      partial.firstDueOffsetInterval = null;
    }
    onChange(partial);
  };

  var handleDateChange = function (_event, value) {
    // The Today/Now quick pick means "whenever this saves" -- an instant, not
    // a calendar date, so it is zone-agnostic and stored as-is.
    if (value === dynamicDate) {
      onChange({ firstDueDate: getTimestampFromDate(new Date()) });
      return;
    }
    // A picked date is a calendar day the user means in the RULE's zone, not
    // wherever the browser happens to sit. InspectionRecurrenceEngine.cs only
    // moves the STORED occurrence to noon when the rule is day-coarse
    // (dayCoarse ? candidate.Date.At(new LocalTime(12, 0)) : candidate) -- the
    // input anchor otherwise keeps its own time of day, and the engine floors
    // candidates against that exact time. A rule naming times of day (e.g.
    // hoursOfDay: [8, 20]) would have a noon anchor fail the floor check for
    // every named hour before noon that day (or push the whole first
    // occurrence to a later day if every hour is before noon) -- the picked
    // date silently becomes a different one. So only a day-coarse rule gets
    // noon; a rule with named hours gets the day's start instead, which
    // excludes none of them.
    var timestamp = isDayCoarse(hoursOfDay)
      ? getZonedTimestamp(value, timeZone)
      : getZonedTimestamp(value, timeZone, 0, 0, 0);
    onChange({ firstDueDate: timestamp });
  };

  var handleFieldChange = function (suuid, title) {
    onChange({ firstDueFieldKey: suuid || null, firstDueFieldTitle: title || '' });
  };

  var handleOffsetIntervalChange = function (e) {
    var value = parseNonNegativeInt(e.target.value);
    if (value === null) {
      onChange({ firstDueOffsetInterval: null });
      return;
    }
    if (value > MAX_FIRST_DUE_OFFSET) value = MAX_FIRST_DUE_OFFSET;
    onChange({ firstDueOffsetInterval: value });
  };

  var handleOffsetUnitChange = function (e) {
    onChange({ firstDueOffsetUnit: e.target.value });
  };

  return (
    <div className="inspect-recurrence-first-due">
      <div className="inspect-recurrence-grid-row">
        <label className="inspect-recurrence-grid-label" htmlFor="inspect-first-due-anchor">
          {t('inspect:firstDueDateLabel')}
        </label>
        <div className="inspect-recurrence-grid-control">
          <select
            id="inspect-first-due-anchor"
            className={'inspect-recurrence-select' + (errors.scheduledFirstDueAnchor ? ' has-error' : '')}
            value={anchor}
            onChange={function (e) {
              handleAnchorChange(e.target.value);
            }}
            aria-label={t('inspect:firstDueDateLabel')}
          >
            {ANCHORS.map(function (a) {
              return (
                <option key={a} value={a}>
                  {t('inspect:firstDueAnchor_' + a)}
                </option>
              );
            })}
          </select>
          {anchor === 'date' && (
            <div className="inspect-first-due-reveal">
              <SlateDatePicker value={pickerDate} onChange={handleDateChange} t={t} hideToday />
            </div>
          )}
          {anchor === 'field' && (
            <div className="inspect-first-due-reveal">
              <FieldSelector
                currentSUUID={fieldKey || ''}
                fields={scopeToAssetFields(settingsFields)}
                onChange={handleFieldChange}
                filterType="dateTime"
                error={!!errors.scheduledFirstDueFieldPathHash}
              />
              <span className="inspect-recurrence-row inspect-first-due-offset">
                <input
                  type="number"
                  className="inspect-recurrence-number"
                  value={offsetInterval === null || offsetInterval === undefined ? '' : offsetInterval}
                  min="0"
                  max={MAX_FIRST_DUE_OFFSET}
                  onChange={handleOffsetIntervalChange}
                  aria-label={t('inspect:firstDueOffsetLabel')}
                />
                <select
                  className="inspect-recurrence-select"
                  value={offsetUnit}
                  onChange={handleOffsetUnitChange}
                  aria-label={t('inspect:firstDueOffsetUnitLabel')}
                >
                  {OFFSET_UNITS.map(function (u) {
                    return (
                      <option key={u} value={u}>
                        {t('inspect:unit_' + u)}
                      </option>
                    );
                  })}
                </select>
                <span className="inspect-recurrence-every-label">
                  {t('inspect:firstDueOffsetSuffix')}
                </span>
              </span>
            </div>
          )}
          {errors.scheduledFirstDueAnchor && (
            <div className="slate-text-input-error">{errors.scheduledFirstDueAnchor}</div>
          )}
          {errors.scheduledFirstDueDate && (
            <div className="slate-text-input-error">{errors.scheduledFirstDueDate}</div>
          )}
          {errors.scheduledFirstDueFieldPathHash && (
            <div className="slate-text-input-error">{errors.scheduledFirstDueFieldPathHash}</div>
          )}
          {errors.scheduledFirstDueOffsetInterval && (
            <div className="slate-text-input-error">{errors.scheduledFirstDueOffsetInterval}</div>
          )}
          {errors.scheduledFirstDueOffsetUnit && (
            <div className="slate-text-input-error">{errors.scheduledFirstDueOffsetUnit}</div>
          )}
          <p className="inspect-mode-hint inspect-recurrence-grid-hint">
            {t('inspect:firstDueAnchorHint_' + anchor)}
          </p>
        </div>
      </div>
    </div>
  );
};

FirstDueDateEditor.propTypes = {
  anchor: PropTypes.string,
  date: PropTypes.number,
  fieldKey: PropTypes.string,
  offsetInterval: PropTypes.number,
  offsetUnit: PropTypes.string,
  settingsFields: PropTypes.array,
  timeZone: PropTypes.string,
  frequency: PropTypes.string,
  hoursOfDay: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default FirstDueDateEditor;
